import { TmContainer } from '@treasury/presentation';
import { TmList } from '@treasury/presentation/components/tm-list';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
    TmFilterForm,
    TmFilterFormApplyEvent,
    TmFilterFormEvent,
    listenForFilterFormEvent,
} from '../forms';

import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-footer';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-list';

export const tagName = 'tm-list-layout';
@customElement(tagName)
export class TmListLayout<T extends object = object> extends TmContainer {
    @property({ attribute: false })
    public columns: TmList<T>['columns'] = [];

    @property({ attribute: false })
    public virtualScrollItemThreshold?: number;

    @property({ attribute: false })
    public searchFields?: TmList<T>['searchFields'];

    @property({ attribute: false })
    public searchPlaceholder?: TmList<T>['searchPlaceholder'];

    @property({ attribute: false })
    public groupBy?: TmList<T>['groupBy'];

    @property({ type: Boolean })
    public multiselect?: TmList<T>['multiselect'];

    @property({ type: Boolean })
    public disableStickyHeaders?: TmList<T>['disableStickyHeaders'];

    @property({ attribute: false })
    public items?: TmList<T>['items'];

    @property({ attribute: false })
    public itemsDescriptor: TmList<T>['itemsDescriptor'] = 'items';

    @property({ attribute: false })
    public fetchItems?: (filter?: unknown) => Promise<TmList<T>['items'] | undefined>;

    @property({ attribute: false })
    public headerTitle?: string;

    @property({ type: String })
    public filterSheetHeading = 'Advanced Filter';

    protected verifiedPropNames: readonly (keyof this)[] = ['columns'];

    @state()
    private filterEl?: TmFilterForm;

    @state()
    private currentFilter?: unknown;

    @state()
    private open = false;

    private filterListenerAdded = false;

    protected render() {
        if (this.loading || !this.items) {
            return html`${this.renderLoader()} ${this.renderFilterSheet()}`;
        }

        return html`<tm-list
                .columns=${this.columns}
                .items=${this.items}
                .itemsDescriptor=${this.itemsDescriptor}
                .headerTitle=${this.headerTitle}
                .searchFields=${this.searchFields}
                .searchPlaceholder=${this.searchPlaceholder}
                .filterButton=${!!this.filterEl}
                .virtualScrollItemThreshold=${this.virtualScrollItemThreshold}
                .groupBy=${this.groupBy}
                ?multiselect=${this.multiselect}
                ?disableStickyHeaders=${this.disableStickyHeaders}
                @open-list-filter=${() => {
                    this.open = true;
                }}
            >
                <slot name="header-subtitle" slot="header-subtitle"></slot>
                <slot name="additional-buttons" slot="additional-buttons"></slot>
            </tm-list>
            ${this.renderFilterSheet()}`;
    }

    protected async updated(changedProps: Map<PropertyKey, unknown>) {
        if (changedProps.has('fetchItems') && !changedProps.get('fetchItems')) {
            this.updateItems();
        }
        if (changedProps.has('items') && this.items) {
            this.loading = false;
        }
    }

    protected onSlotChange(e: Event) {
        // Detect and store the slotted component, making sure we only use a TmFilterForm component
        if (e.target instanceof HTMLSlotElement && e.target.assignedNodes().length > 0) {
            const filterForm = e.target
                .assignedNodes()
                // whitespace in html counts as separate slotted nodes so filter it out
                .filter(n => n instanceof TmFilterForm) as TmFilterForm[];

            this.listenForFilterEvents(filterForm);
        }
    }

    private listenForFilterEvents(filterForm: TmFilterForm[]) {
        if (filterForm.length && !this.filterListenerAdded) {
            const [firstForm] = filterForm;
            this.filterEl = firstForm;
            const { unsubscribe } = listenForFilterFormEvent(
                this.filterEl,
                TmFilterFormEvent.Apply,
                e => this.onApplyFilter(e)
            );
            this.filterListenerAdded = true;

            this.filterEl.applyFilter();

            this.addCleanupTasks(unsubscribe);
        }
    }

    private async updateItems() {
        if (this.fetchItems) {
            await this.tryFetch(this.fetchItems.bind(this, this.currentFilter), items => {
                this.items = items;
            });
        }
    }

    private onApplyFilter(e: TmFilterFormApplyEvent<unknown>) {
        this.currentFilter = e.detail;
        this.updateItems();
    }

    private renderFilterSheet() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${(e: CustomEvent) => {
                this.open = false;
                e.stopPropagation();
            }}
            ><p slot="header-center">${this.filterSheetHeading}</p>
            <!-- A slot is required for the FilterForm because they can vary drastically. Because
            the footer lives outside the FilterForm but relies on the validity of the FilterForm, we
            need to call requestUpdate() each time the validity changes so that the footer can
            request the current value and update its buttons accordingly. -->
            <tm-section class="p-4" @change-validity=${() => this.requestUpdate()}>
                <slot @slotchange=${this.onSlotChange}></slot>
            </tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: (e: Event) => {
                            this.filterEl?.applyFilter();
                            e.currentTarget?.dispatchEvent(
                                new CustomEvent('close', { bubbles: true, composed: true })
                            );
                        },
                        disabled: !this.filterEl || !this.filterEl.isValid,
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.filterEl?.reset();
                        },
                    },
                ]}
            ></tm-footer>
        </tm-bottom-sheet>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    flex: 1 1 auto;
                    overflow-y: hidden;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmListLayout<object>;
    }
}
